export function reportCalendarDate(value) {
  // Report dates are calendar keys, including timestamp-shaped legacy keys.
  const match = typeof value === 'string' && value.match(/^(\d{4}-\d{2}-\d{2})(?:$|[ T])/);
  if (match) return match[1];
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error('报告日期无效');
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * 机构面板优化 API
 * 使用后端优化接口，减少数据传输量，提升性能
 */

import { createAxiosInstance } from './axios.js'

/**
 * 获取设备列表（使用新的API服务器）
 * @param {Object} options - 查询选项
 * @param {string} options.filter - 过滤条件
 * @param {number} options.page - 页码
 * @param {number} options.pagesize - 每页数量
 * @returns {Promise<Object>} 设备列表数据
 */
export async function getDeviceList(options = {}) {
  const {
    filter = '',
    page = 1,
    pagesize = 50
  } = options

  const axios = createAxiosInstance('API服务器')
  
  try {
    // GET 请求，和原接口一致
    const response = await axios.get('/api/v1/orgs/devices', {
      params: {
        filter: filter,
        page: page,
        pagesize: pagesize
      }
    })
    
    return response.data
  } catch (error) {
    console.error('获取设备列表失败:', error)
    throw error
  }
}

/**
 * 批量获取设备报告统计
 * @param {Array<string>} devices - 设备ID列表
 * @param {number} reportDays - 统计最近N天
 * @returns {Promise<Object>} 报告统计数据
 */
export async function getDeviceReportsBatch(devices, reportDays = 60) {
  const axios = createAxiosInstance('API服务器')
  
  try {
    const response = await axios.post('/api/v1/orgs/devices/reports/batch', {
      devices: devices,
      report_days: reportDays
    })
    
    return response.data
  } catch (error) {
    console.error('批量获取报告统计失败:', error)
    throw error
  }
}

/**
 * 获取完整的设备列表（含报告统计）
 * 使用两个接口并行请求，然后合并数据
 * @param {Object} options - 查询选项
 * @returns {Promise<Object>} 完整的设备数据
 */
export async function getOptimizedDeviceList(options = {}) {
  const {
    filter = '',
    page = 1,
    pagesize = 50,
    includeReports = true,
    reportDays = 60
  } = options

  try {
    // 先获取设备列表
    const deviceData = await getDeviceList({ filter, page, pagesize })
    
    // 如果不需要报告统计，直接返回
    if (!includeReports || !deviceData.data || !deviceData.data.nodes || deviceData.data.nodes.length === 0) {
      return deviceData
    }
    
    // 提取设备ID列表
    const deviceIds = deviceData.data.nodes.map(node => node.device)
    
    // 并行获取报告统计
    try {
      const reportsData = await getDeviceReportsBatch(deviceIds, reportDays)
      
      // 合并数据：将报告数量添加到每个设备
      deviceData.data.nodes.forEach(node => {
        node.reports = reportsData.data[node.device] || 0
      })
      
      console.log('设备列表和报告统计合并完成')
    } catch (error) {
      console.warn('获取报告统计失败，使用默认值:', error)
      // 报告统计失败不影响设备列表显示
      deviceData.data.nodes.forEach(node => {
        node.reports = 0
      })
    }
    
    return deviceData
  } catch (error) {
    console.error('获取设备列表失败:', error)
    throw error
  }
}

/**
 * 将优化接口返回的数据转换为前端原有格式
 * @param {Object} optimizedData - 优化接口返回的数据
 * @returns {Object} 转换后的数据，兼容原有格式
 */
export function convertOptimizedDataToLegacy(optimizedData) {
  // ✅ 新接口返回格式： { data: { nodes: [], count: 100 } }
  // 前端需要格式： { nodes: [], count: 100 }
  if (optimizedData.data) {
    return {
      nodes: optimizedData.data.nodes || [],
      count: optimizedData.data.count || 0
    }
  }
  
  // 降级处理
  return {
    nodes: optimizedData.nodes || [],
    count: optimizedData.count || 0
  }
}

/**
 * 获取报告趋势数据（优化版）
 * @param {string} device - 设备ID
 * @param {Object} options - 选项
 * @param {number} options.days - 获取最近N天
 * @param {string} options.endDate - 结束日期 YYYY-MM-DD
 * @returns {Promise<Object>} 趋势数据
 */
export async function getReportTrendData(device, options = {}) {
  const { days = 30, endDate = null, anchorDate = null } = options
  
  // The same partner endpoint is available through the authenticated SPA proxy.
  const axios = createAxiosInstance('数据服务器')
  
  try {
    let url = `/api/v1/orgs/devices/${device}/reports/trend?days=${days}`
    if (endDate) {
      url += `&end_date=${endDate}`
    }
    
    if (anchorDate) {
      url += `&anchor_date=${encodeURIComponent(anchorDate)}`
    }
    const response = await axios.get(url)
    if (anchorDate && response.data?.anchor_date !== anchorDate) {
      throw new Error('个人趋势接口未确认受试者隔离');
    }
    return response.data
  } catch (error) {
    console.error('获取趋势数据失败:', error)
    throw error
  }
}

/**
 * 获取用户报告趋势数据（基于 user_id，用于干预枕）
 * @param {string} userId - 用户ID（手机号或微信openid）
 * @param {Object} options - 选项
 * @param {number} options.days - 获取最近N天
 * @param {string} options.endDate - 结束日期 YYYY-MM-DD
 * @returns {Promise<Object>} 趋势数据
 */
export async function getUserReportTrendData(userId, options = {}) {
  const { days = 30, endDate = null } = options
  
  const axios = createAxiosInstance('API服务器')  // 使用新的API服务器
  
  try {
    let url = `/api/v1/orgs/users/${userId}/reports/trend?days=${days}`
    if (endDate) {
      url += `&end_date=${endDate}`
    }
    
    const response = await axios.get(url)
    return response.data
  } catch (error) {
    console.error('获取用户趋势数据失败:', error)
    throw error
  }
}

/**
 * 批量获取多个设备的趋势数据
 * @param {Array<string>} devices - 设备ID列表
 * @param {Object} options - 选项
 * @param {number} options.days - 获取最近N天
 * @param {string} options.endDate - 结束日期 YYYY-MM-DD
 * @returns {Promise<Object>} 批量趋势数据
 */
export async function getBatchReportTrend(devices, options = {}) {
  const { days = 30, endDate = null } = options
  
  const axios = createAxiosInstance('API服务器')  // 使用新的API服务器
  
  try {
    const response = await axios.post('/api/v1/orgs/reports/trend/batch', {
      devices: devices,
      days: days,
      end_date: endDate
    })
    
    return response.data
  } catch (error) {
    console.error('批量获取趋势数据失败:', error)
    throw error
  }
}

/**
 * 将趋势数据转换为原有格式（兼容旧代码）
 * 旧接口返回格式: { "action": "devicereport", "data": { "11/7": { tst: 420, ... }, ... } }
 * 图表期望格式: { "11/7": { tst: 420, tib: 480, start: ..., end: ..., ... }, ... }
 * 注意：旧接口直接返回 online 字段的数据（扁平化），不是嵌套在 online 和 summary 中
 * @param {Object} trendData - 优化接口返回的趋势数据
 * @returns {Object} 转换后的数据对象，key 为日期 "M/D" 格式
 */
export function convertTrendDataToLegacy(trendData) {
  const { reports } = trendData
  
  // 转换为对象格式，key 为日期 "M/D"
  const result = {}
  
  reports.forEach(report => {
    const dateKey = report.date_key || (() => {
      // 如果没有 date_key，从 date 计算
      const date = new Date(report.date)
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${month}/${day}`
    })()
    
    // 扁平化数据，直接返回所有字段（模拟旧接口的 online 数据格式）
    // 注意：图表需要特定的字段名，必须保持一致性
    result[dateKey] = {
      // Keep stored health values and full calendar identity, including valid zero.
      date: report.date,
      sleep_age_avg_21day: report.sleep_age_avg_21day ?? null,
      aging_pace_raw: report.aging_pace_raw ?? null,
      aging_pace_avg_28d: report.aging_pace_avg_28d ?? null,
      recoveryScore: report.recoveryScore ?? null,
      start: report.start,
      end: report.end,
      report_time: report.report_time,
      tst: report.tst,
      tib: report.tib,
      sol: report.sol,
      waso: report.waso,
      se: report.se,
      deep_time: report.deep_time,
      rem_time: report.rem_time,
      core_time: report.core_time,
      sleep_dur_1: report.sleep_dur_1,
      sleep_dur_2: report.sleep_dur_2 || 0,  // REM时长 - 快速眼动和深睡时长图表需要
      sleep_dur_3: report.sleep_dur_3 || 0,  // N1时长 - 睡眠阶段组成图表需要
      sleep_dur_4: report.sleep_dur_4 || 0,  // N2时长 - 睡眠阶段组成图表需要
      sleep_dur_5: report.sleep_dur_5 || 0,  // 深睡时长 - 快速眼动和深睡时长图表需要
      ahi: report.ahi,
      oahi: report.oahi,
      OAHI: report.oahi,
      ahi_2: report.ahi_2,
      ahi_345: report.ahi_345,
      oahi_2: report.oahi_2,
      oahi_345: report.oahi_345,
      sri: report.sri,
      sfi: report.sfi,
      quality: report.quality,
      avg_resp: report.avg_rr || report.avg_resp || 0,
      resp_limit_max: report.resp_limit_max || 0,
      resp_limit_min: report.resp_limit_min || 0,
      bcgsnore_index: report.bcgsnore_index,
      
      // 心率相关 - 睡眠心率图表需要
      bpm_avg: report.bpm_avg || report.avg_hr || 0,
      bpm_min: report.bpm_min || report.min_hr || 0,
      bpm_limit_max: report.bpm_limit_max || 0,  // 基线最大值
      bpm_limit_min: report.bpm_limit_min || 0,  // 基线最小值
      avg_sleep_heart_rate: report.bpm_avg || report.avg_hr || 0,  // 兼容字段
      min_sleep_heart_rate: report.bpm_min || report.min_hr || 0,  // 兼容字段
      
      // HRV相关 - 睡眠心率变异性图表需要
      hrv_rmssd_mean: report.hrv_rmssd_mean || report.avg_hrv_rmssd || 0,
      weekly_avg_hrv_rmssd: report.weekly_avg_hrv_rmssd || 0,  // 7日均值
      hrv_limit_max: report.hrv_limit_max || 0,  // HRV基线最大值
      hrv_limit_min: report.hrv_limit_min || 0,  // HRV基线最小值
      avg_hrv_rmssd: report.hrv_rmssd_mean || report.avg_hrv_rmssd || 0,  // 兼容字段
      avg_hrv_radio: report.avg_hrv_radio || 0,
      
      // 环境相关 - 睡眠环境图表需要（注意字段名）
      avg_temp: report.avg_temp || report.avg_temperature || 0,  // 图表需要 avg_temp
      avg_humi: report.avg_humi || report.avg_humidity || 0,  // 图表需要 avg_humi
      high_illu: report.high_illu || report.high_illuminance || 0,  // 强光照
      avg_temperature: report.avg_temp || report.avg_temperature || 0,  // 兼容字段
      avg_humidity: report.avg_humi || report.avg_humidity || 0  // 兼容字段
    }
  })
  
  return result
}

/**
 * 批量获取设备关注意图运行态。
 * @param {Array<string>} deviceIds - 当前页面可见设备 ID
 * @returns {Promise<Object>} device_id -> attention state
 */
export async function getAttentionBatch(deviceIds) {
  if (!Array.isArray(deviceIds) || deviceIds.length === 0) {
    return {}
  }
  const axios = createAxiosInstance('API服务器')
  const response = await axios.post('/attention/batch', { device_ids: deviceIds })
  return response.data?.data?.items || {}
}

/**
 * 性能对比工具 - 用于测试新旧接口性能差异
 */
export async function performanceCompare(filter, page, pagesize) {
  const startOld = performance.now()
  let oldData = null
  try {
    // 调用旧接口
    const { request_data } = await import('./axios.js')
    oldData = await request_data(
      `/device/?filter=${encodeURIComponent(filter)}&page=${page}&pagesize=${pagesize}`
    )
  } catch (error) {
    console.error('旧接口调用失败:', error)
  }
  const oldTime = performance.now() - startOld
  
  const startNew = performance.now()
  let newData = null
  try {
    // 调用新接口
    newData = await getOptimizedDeviceList({ filter, page, pagesize })
  } catch (error) {
    console.error('新接口调用失败:', error)
  }
  const newTime = performance.now() - startNew
  
  // 计算数据大小（估算）
  const oldSize = JSON.stringify(oldData || {}).length
  const newSize = JSON.stringify(newData || {}).length
  
  console.log('=== 性能对比结果 ===')
  console.log(`旧接口耗时: ${oldTime.toFixed(2)}ms`)
  console.log(`新接口耗时: ${newTime.toFixed(2)}ms`)
  console.log(`性能提升: ${((oldTime - newTime) / oldTime * 100).toFixed(1)}%`)
  console.log(`旧接口数据大小: ${(oldSize / 1024).toFixed(2)} KB`)
  console.log(`新接口数据大小: ${(newSize / 1024).toFixed(2)} KB`)
  console.log(`数据量减少: ${((oldSize - newSize) / oldSize * 100).toFixed(1)}%`)
  
  return {
    oldTime,
    newTime,
    oldSize,
    newSize,
    performanceGain: ((oldTime - newTime) / oldTime * 100).toFixed(1),
    dataSizeReduction: ((oldSize - newSize) / oldSize * 100).toFixed(1)
  }
}

